import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowUpRight, Heart, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Price } from "@/context/CurrencyContext";

const COUNTRY_FLAGS = {
  India: "🇮🇳", Nepal: "🇳🇵", Bhutan: "🇧🇹", "Sri Lanka": "🇱🇰",
  Maldives: "🇲🇻", Thailand: "🇹🇭", UAE: "🇦🇪",
};

export default function TourCard({ tour, span = "default" }) {
  const { user, refresh } = useAuth();
  const [saved, setSaved] = useState(user?.wishlist?.includes(tour.slug));
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    setSaved(!!user?.wishlist?.includes(tour.slug));
  }, [user, tour.slug]);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save tours.");
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/wishlist/${tour.slug}`);
        setSaved(false);
        toast.success("Removed from wishlist");
      } else {
        await api.post("/wishlist", { tour_slug: tour.slug });
        setSaved(true);
        toast.success("Saved to wishlist");
      }
      refresh();
    } catch (_) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const aspect = span === "wide" ? "aspect-[16/10] md:aspect-[2/1]" : "aspect-[4/5]";
  const priceUsd = tour.price_usd || Math.round((tour.price_from || 0) / 83);
  const flag = COUNTRY_FLAGS[tour.country] || "🌏";

  return (
    <Link
      to={`/tours/${tour.slug}`}
      data-testid={`tour-card-${tour.slug}`}
      className="group relative block overflow-hidden rounded-sm border border-cream-200 bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 hover:border-marigold/40 transition-all duration-500 ease-out"
    >
      <div className={`relative overflow-hidden ${aspect}`}>
        <img
          src={tour.image_url}
          alt={tour.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

        {/* Top tags */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-saffron/95 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm whitespace-nowrap">
              {tour.category.replace(/-/g, " ")}
            </span>
            <span className="px-2.5 py-1 bg-white/90 backdrop-blur text-foreground text-xs rounded-sm flex items-center gap-1 font-medium" data-testid={`country-${tour.slug}`}>
              <span className="text-base leading-none">{flag}</span> {tour.country}
            </span>
          </div>
          <button
            onClick={toggleSave}
            disabled={busy}
            data-testid={`wishlist-card-${tour.slug}`}
            aria-label="Save to wishlist"
            className={`w-9 h-9 rounded-sm backdrop-blur flex items-center justify-center transition-colors ${saved ? "bg-marigold text-deepred" : "bg-white/20 text-white hover:bg-white hover:text-deepred"}`}
          >
            <Heart className={`w-4 h-4 ${saved ? "fill-deepred" : ""}`} />
          </button>
        </div>

        <div className="absolute bottom-0 inset-x-0 p-6">
          <h3 className="font-serif text-2xl md:text-[26px] text-white leading-tight tracking-tight mb-2">{tour.title}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/85 mb-3">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {tour.duration}</span>
            {tour.locations?.length > 0 && (
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {tour.locations.slice(0, 3).join(" · ")}</span>
            )}
            {tour.difficulty_level && (
              <span className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> {tour.difficulty_level}</span>
            )}
          </div>
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-marigold">Price starts from</div>
              <div className="font-serif text-2xl text-white" data-testid={`price-${tour.slug}`}>
                <Price usd={priceUsd} />
                <span className="text-xs text-white/70 ml-1">/ person</span>
              </div>
            </div>
            <div className="px-3 py-2 rounded-sm bg-white/15 backdrop-blur border border-white/30 group-hover:bg-saffron group-hover:border-saffron group-hover:shadow-lg group-hover:pl-4 transition-all duration-300 text-xs uppercase tracking-wider text-white flex items-center gap-1.5">
              View Details <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform duration-300" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
